import { ChangeEvent, DragEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ColorCube } from "./ColorCube";
import {
  applySpectraFitRecipe,
  applySpectraFitRecipeToImageData,
  fitMatrixRecipe,
  formatSpectraFitRecipe,
  parseSpectraFitRecipe,
  SpectraFitRecipe,
  tuneSpectraFitRecipe
} from "./fit";
import { clearLutHistory, listLutHistory, LutHistoryItem, saveLutHistoryItem } from "./history";
import {
  applyLutToImageData,
  applyLutToRgb,
  colorPatches,
  CubeLut,
  formatBytes,
  parseCubeLut,
  summarizeLut
} from "./lut";

const sampleImage = "/sample-iphone.jpg";
const defaultLutUrl = "/x100vi-eterna-33grid.cube";
const defaultLutName = "X100VI_FLog_FGamut_to_ETERNA_BT.709_33grid_V.1.00.cube";
const defaultRecipeText = `SpectraFit v1
Exposure：-3.97
Contrast：-12
Gamma R：1.00
Gamma G：1.00
Gamma B：1.00
Saturation：+51
Temperature：+6.88
Tint：+8.02
RGB Bias：-3.64 -22 -5.06
Matrix：
0.8867 0.2437 -0.0760
0.0323 1.1406 0.0333
-0.0518 0.5019 0.6171
Fit Score：13
Mean RGB Error：43.62
Max RGB Error：147.17`;
const defaultRecipe = parseSpectraFitRecipe(defaultRecipeText);

type Language = "en" | "zh";
type ActiveTab = "restore" | "apply" | "create";
type RecipeNumberField = "exposure" | "contrast" | "saturation" | "temperature" | "tint";

interface RecipeHistoryItem {
  id: string;
  createdAt: number;
  text: string;
  recipe: SpectraFitRecipe;
}

interface BuiltInLookDefinition {
  id: string;
  displayName: string;
  sourceProfile: string;
  fileName: string;
  url: string;
}

interface BuiltInLookState {
  definition: BuiltInLookDefinition;
  lut: CubeLut | null;
  recipe: SpectraFitRecipe | null;
  recipeText: string;
  error: string | null;
}

interface UploadedImageItem {
  id: string;
  file: File;
  fileName: string;
  objectUrl: string;
}

const recipeHistoryStorageKey = "spectracam.recipeHistory.v1";
const recipeHistoryLimit = 8;
const builtInLookStrength = 1;

const builtInLookDefinitions: BuiltInLookDefinition[] = [
  {
    id: "flog-eterna-bb",
    displayName: "ETERNA Bleach Bypass",
    sourceProfile: "F-Log / F-Gamut",
    fileName: "X100VI_FLog_FGamut_to_ETERNA-BB_BT.709_33grid_V.1.00.cube",
    url: "/luts/x100vi/X100VI_FLog_FGamut_to_ETERNA-BB_BT.709_33grid_V.1.00.cube"
  },
  {
    id: "flog-eterna",
    displayName: "ETERNA",
    sourceProfile: "F-Log / F-Gamut",
    fileName: "X100VI_FLog_FGamut_to_ETERNA_BT.709_33grid_V.1.00.cube",
    url: "/luts/x100vi/X100VI_FLog_FGamut_to_ETERNA_BT.709_33grid_V.1.00.cube"
  },
  {
    id: "flog-flog",
    displayName: "F-Log Normalized",
    sourceProfile: "F-Log / F-Gamut",
    fileName: "X100VI_FLog_FGamut_to_FLog_BT.709_33grid_V.1.00.cube",
    url: "/luts/x100vi/X100VI_FLog_FGamut_to_FLog_BT.709_33grid_V.1.00.cube"
  },
  {
    id: "flog-wdr",
    displayName: "Wide Dynamic Range",
    sourceProfile: "F-Log / F-Gamut",
    fileName: "X100VI_FLog_FGamut_to_WDR_BT.709_33grid_V.1.00.cube",
    url: "/luts/x100vi/X100VI_FLog_FGamut_to_WDR_BT.709_33grid_V.1.00.cube"
  },
  {
    id: "flog2-eterna-bb",
    displayName: "ETERNA Bleach Bypass",
    sourceProfile: "F-Log2 / F-Gamut",
    fileName: "X100VI_FLog2_FGamut_to_ETERNA-BB_BT.709_33grid_V.1.00.cube",
    url: "/luts/x100vi/X100VI_FLog2_FGamut_to_ETERNA-BB_BT.709_33grid_V.1.00.cube"
  },
  {
    id: "flog2-eterna",
    displayName: "ETERNA",
    sourceProfile: "F-Log2 / F-Gamut",
    fileName: "X100VI_FLog2_FGamut_to_ETERNA_BT.709_33grid_V.1.00.cube",
    url: "/luts/x100vi/X100VI_FLog2_FGamut_to_ETERNA_BT.709_33grid_V.1.00.cube"
  },
  {
    id: "flog2-flog2",
    displayName: "F-Log2 Normalized",
    sourceProfile: "F-Log2 / F-Gamut",
    fileName: "X100VI_FLog2_FGamut_to_FLog2_BT.709_33grid_V.1.00.cube",
    url: "/luts/x100vi/X100VI_FLog2_FGamut_to_FLog2_BT.709_33grid_V.1.00.cube"
  },
  {
    id: "flog2-wdr",
    displayName: "Wide Dynamic Range",
    sourceProfile: "F-Log2 / F-Gamut",
    fileName: "X100VI_FLog2_FGamut_to_WDR_BT.709_33grid_V.1.00.cube",
    url: "/luts/x100vi/X100VI_FLog2_FGamut_to_WDR_BT.709_33grid_V.1.00.cube"
  }
];

const copy = {
  en: {
    title: "Spectra Studio",
    restoreTab: "Spectrum library",
    applyTab: "Apply spectrum",
    createTab: "Create spectrum",
    restoreLead: "Browse built-in Fuji spectra, compare each LUT against its compact SpectraFit recovery, then copy a shareable spectrum.",
    applyLead: "Paste a shared spectrum, upload your own photos, preview the recovered result, then download the processed images.",
    createLead: "Upload a .cube LUT, fit it into a shareable SpectraFit spectrum, then copy it as plain text.",
    applyPhotosTitle: "Apply shared spectrum to photos",
    applyPhotosLead: "Paste SpectraFit v1 spectrum text, upload local images, and process everything in this browser.",
    applySharedLook: "Apply Spectrum",
    uploadImages: "Upload images",
    addImages: "Add images",
    clearImages: "Clear images",
    downloadAll: "Download all",
    downloadImage: "Download",
    processed: "Processed",
    photosReady: (count: string) => `${count} photos ready`,
    noPhotos: "Upload images to preview the recovered spectrum.",
    applyErrorFallback: "Unable to parse this SpectraFit text.",
    lookLibraryTitle: "Built-in shareable spectra",
    lookLibraryLead: "Each row is generated from a Fuji X100VI LUT. Copy the SpectraFit text to share the spectrum in notes, Xiaohongshu, or chat.",
    lookLibraryStatus: (loaded: string, total: string) => `${loaded}/${total} spectra ready`,
    presetSpectraTitle: "Preset LUTs",
    presetSpectraLead: "Start from a built-in Fuji LUT, then tune and copy the generated spectrum.",
    presetLoading: "Loading presets",
    rowNumber: "#",
    name: "Name",
    loadingLook: "Loading spectrum",
    sharePanelTitle: "Shareable Spectrum",
    sharePanelSubtitle: "Copy this SpectraFit text into Xiaohongshu notes, chat, or product presets.",
    sharePanelEmpty: "Upload a LUT to generate a shareable spectrum.",
    recipeHistory: "Spectrum history",
    recipeHistoryEmpty: "Imported spectra will appear here after you apply them.",
    tuneRecipe: "Tune spectrum",
    resetFit: "Reset to fit",
    exposure: "Exposure",
    contrast: "Contrast",
    saturation: "Saturation",
    temperature: "Temperature",
    tint: "Tint",
    redBias: "Red Bias",
    greenBias: "Green Bias",
    blueBias: "Blue Bias",
    importHeroTitle: "Paste shared spectrum",
    importHeroText: "Visitors arriving with SpectraFit v1 text should start here. Paste, import, and compare the recovered spectrum instantly.",
    useSampleRecipe: "Use Sample",
    defaultPreset: "Built-in Fuji ETERNA spectrum",
    creatorTools: "Creator tools",
    loaded: "grid loaded",
    localParser: "local browser parser",
    dropLabel: "DROP .CUBE LUT",
    uploadLut: "Upload LUT file",
    parsedLocal: "Parsed in browser. No server upload.",
    dragLut: "Drag a LUT here or click to select one.",
    titleMetric: "Title",
    grid: "Grid",
    entries: "Entries",
    size: "Size",
    waiting: "Waiting",
    lutStrength: "LUT strength",
    analysisNote: "Analysis note",
    recentLuts: "Recent LUTs",
    clear: "Clear",
    historyEmpty: "Imported LUTs stay local in this browser.",
    recipeImport: "Spectrum Import",
    useGenerated: "Use Generated Spectrum",
    recipePlaceholder: "Paste SpectraFit v1 spectrum text...",
    importRecipe: "Import Spectrum",
    importStatus: "Paste shared text to rebuild a compact spectrum locally.",
    importedStatus: (score: string, error: string) => `Imported · score ${score} · mean error ${error}`,
    originalSample: "Original sample",
    iphoneSource: "iPhone source image",
    renderedOutput: "Rendered output",
    neutral: "neutral",
    lutMix: (value: number) => `${value}% LUT mix`,
    mix: (value: number) => `${value}% mix`,
    colorTransform: "3D color transform",
    mappedCloud: "mapped color point cloud",
    neutralCube: "neutral cube placeholder",
    colorResponse: "Color response",
    responseLegend: "before / lut / fit",
    fittedRecipe: "Fitted Spectrum",
    fitScore: (value: string) => `${value} fit score`,
    meanError: "Mean Error",
    maxError: "Max Error",
    copied: "Copied",
    copyFailed: "Copy failed",
    copyRecipe: "Copy Spectrum",
    copyLook: "Copy Spectrum",
    recipeEmpty: "Upload a LUT to generate a compact SpectraFit v1 spectrum.",
    recoveryComparison: "Spectrum recovery",
    comparisonLegend: "original / LUT / SpectraFit spectrum",
    original: "Original",
    sourceSample: "source sample",
    image: "Image",
    sampleName: "Shot on iPhone sample",
    transform: "Transform",
    none: "None",
    role: "Role",
    baselineReference: "Baseline reference",
    originalLut: "Original LUT",
    noLutLoaded: "No LUT loaded",
    strength: "Strength",
    recoveredRecipe: "Spectrum Recovery",
    version: "Version",
    copyImportedRecipe: "Copy Imported Spectrum",
    switchLanguage: "中文"
  },
  zh: {
    title: "光谱工作台",
    restoreTab: "光谱库",
    applyTab: "应用光谱",
    createTab: "制作光谱",
    restoreLead: "浏览内置 Fuji 光谱，对比原始 LUT 与 SpectraFit 复原效果，然后直接复制可分享光谱。",
    applyLead: "粘贴分享来的光谱，上传自己的图片，预览复原效果，并下载处理后的图片。",
    createLead: "上传 .cube LUT，将它拟合成可分享的 SpectraFit 光谱，再以纯文本分享。",
    applyPhotosTitle: "把分享光谱应用到图片",
    applyPhotosLead: "粘贴 SpectraFit v1 光谱文本，批量上传本地图片，并在浏览器本地完成处理。",
    applySharedLook: "应用光谱",
    uploadImages: "上传图片",
    addImages: "添加图片",
    clearImages: "清空图片",
    downloadAll: "下载全部",
    downloadImage: "下载",
    processed: "处理后",
    photosReady: (count: string) => `${count} 张图片已就绪`,
    noPhotos: "上传图片后，可以预览分享光谱的复原效果。",
    applyErrorFallback: "无法解析这段 SpectraFit 文本。",
    lookLibraryTitle: "内置可分享光谱",
    lookLibraryLead: "每一行都由 Fuji X100VI LUT 生成。复制 SpectraFit 文本后，可分享到小红书笔记、聊天或产品预设。",
    lookLibraryStatus: (loaded: string, total: string) => `${loaded}/${total} 个光谱已就绪`,
    presetSpectraTitle: "预置 LUT",
    presetSpectraLead: "直接选择内置 Fuji LUT，再微调并复制生成的光谱。",
    presetLoading: "预设载入中",
    rowNumber: "#",
    name: "名称",
    loadingLook: "光谱载入中",
    sharePanelTitle: "可分享光谱",
    sharePanelSubtitle: "复制这段 SpectraFit 文本，可发到小红书笔记、聊天或产品预设里。",
    sharePanelEmpty: "上传 LUT 后会生成可分享光谱。",
    recipeHistory: "光谱历史",
    recipeHistoryEmpty: "成功导入的光谱会显示在这里。",
    tuneRecipe: "微调光谱",
    resetFit: "重置为拟合",
    exposure: "曝光",
    contrast: "对比",
    saturation: "饱和",
    temperature: "色温",
    tint: "色调",
    redBias: "红色偏移",
    greenBias: "绿色偏移",
    blueBias: "蓝色偏移",
    importHeroTitle: "粘贴分享光谱",
    importHeroText: "带着 SpectraFit v1 光谱文本来的用户从这里开始。粘贴、导入，然后立即对比复原效果。",
    useSampleRecipe: "使用示例",
    defaultPreset: "内置 Fuji ETERNA 光谱",
    creatorTools: "创作者工具",
    loaded: "网格已载入",
    localParser: "浏览器本地解析",
    dropLabel: "拖入 .CUBE LUT",
    uploadLut: "上传 LUT 文件",
    parsedLocal: "已在浏览器本地解析，不上传服务器。",
    dragLut: "拖入 LUT，或点击选择文件。",
    titleMetric: "名称",
    grid: "网格",
    entries: "条目",
    size: "大小",
    waiting: "等待中",
    lutStrength: "LUT 强度",
    analysisNote: "分析备注",
    recentLuts: "历史 LUT",
    clear: "清空",
    historyEmpty: "导入过的 LUT 会保存在当前浏览器本地。",
    recipeImport: "光谱导入",
    useGenerated: "使用生成光谱",
    recipePlaceholder: "粘贴 SpectraFit v1 光谱文本...",
    importRecipe: "导入光谱",
    importStatus: "粘贴分享文本后，可在本地复原紧凑光谱。",
    importedStatus: (score: string, error: string) => `已导入 · 分数 ${score} · 平均误差 ${error}`,
    originalSample: "原始样张",
    iphoneSource: "iPhone 原图样张",
    renderedOutput: "渲染输出",
    neutral: "中性",
    lutMix: (value: number) => `${value}% LUT 混合`,
    mix: (value: number) => `${value}% 混合`,
    colorTransform: "3D 色彩变换",
    mappedCloud: "映射后的色彩点云",
    neutralCube: "中性色彩立方体",
    colorResponse: "色彩响应",
    responseLegend: "原始 / LUT / 拟合",
    fittedRecipe: "拟合光谱",
    fitScore: (value: string) => `${value} 拟合分`,
    meanError: "平均误差",
    maxError: "最大误差",
    copied: "已复制",
    copyFailed: "复制失败",
    copyRecipe: "复制光谱",
    copyLook: "复制光谱",
    recipeEmpty: "上传 LUT 后会生成一组紧凑的 SpectraFit v1 光谱。",
    recoveryComparison: "光谱复原",
    comparisonLegend: "原图 / LUT / SpectraFit 光谱",
    original: "原图",
    sourceSample: "源样张",
    image: "图片",
    sampleName: "iPhone 拍摄样张",
    transform: "变换",
    none: "无",
    role: "用途",
    baselineReference: "基准参考",
    originalLut: "原始 LUT",
    noLutLoaded: "未载入 LUT",
    strength: "强度",
    recoveredRecipe: "光谱复原",
    version: "版本",
    copyImportedRecipe: "复制导入光谱",
    switchLanguage: "EN"
  }
} satisfies Record<Language, Record<string, string | ((...args: never[]) => string)>>;

export default function App() {
  const [language, setLanguage] = useState<Language>(() => {
    const lang = new URLSearchParams(window.location.search).get("lang");
    return lang === "en" ? "en" : "zh";
  });
  const [activeTab, setActiveTab] = useState<ActiveTab>("restore");
  const [lut, setLut] = useState<CubeLut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [strength, setStrength] = useState(0.82);
  const [isDragging, setIsDragging] = useState(false);
  const [history, setHistory] = useState<LutHistoryItem[]>([]);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const [recipeImportText, setRecipeImportText] = useState(defaultRecipeText);
  const [importedRecipe, setImportedRecipe] = useState<SpectraFitRecipe | null>(defaultRecipe);
  const [recipeHistory, setRecipeHistory] = useState<RecipeHistoryItem[]>([]);
  const [creatorRecipe, setCreatorRecipe] = useState<SpectraFitRecipe | null>(null);
  const [recipeImportError, setRecipeImportError] = useState<string | null>(null);
  const [importCopyState, setImportCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const [builtInLooks, setBuiltInLooks] = useState<BuiltInLookState[]>(
    builtInLookDefinitions.map((definition) => ({
      definition,
      lut: null,
      recipe: null,
      recipeText: "",
      error: null
    }))
  );
  const [copiedLookId, setCopiedLookId] = useState<string | null>(null);
  const [applyRecipeText, setApplyRecipeText] = useState(defaultRecipeText);
  const [applyRecipe, setApplyRecipe] = useState<SpectraFitRecipe>(defaultRecipe);
  const [applyRecipeError, setApplyRecipeError] = useState<string | null>(null);
  const [uploadedImages, setUploadedImages] = useState<UploadedImageItem[]>([]);
  const sourceCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const outputCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const compareOriginalCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const compareLutCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const compareRecipeCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const t = copy[language];
  const strengthPercent = Math.round(strength * 100);

  const loadFile = useCallback(async (file: File) => {
    setError(null);
    try {
      const text = await file.text();
      const parsed = parseCubeLut(text, file.name, file.size);
      setLut(parsed);
      setHistory(await saveLutHistoryItem(parsed, text));
    } catch (event) {
      setLut(null);
      setError(event instanceof Error ? event.message : "Unable to parse this LUT.");
    }
  }, []);

  const loadHistoryItem = useCallback((item: LutHistoryItem) => {
    setError(null);
    try {
      setLut(parseCubeLut(item.sourceText, item.fileName, item.fileSize));
    } catch (event) {
      setError(event instanceof Error ? event.message : "Unable to restore this LUT.");
    }
  }, []);

  const clearHistory = useCallback(async () => {
    await clearLutHistory();
    setHistory([]);
  }, []);

  const handleFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) void loadFile(file);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void loadFile(file);
  };

  const handleImageInput = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []).filter((file) => file.type.startsWith("image/"));
    if (files.length === 0) return;
    setUploadedImages((current) => [
      ...current,
      ...files.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        file,
        fileName: file.name,
        objectUrl: URL.createObjectURL(file)
      }))
    ]);
    event.target.value = "";
  };

  useEffect(() => {
    void listLutHistory().then(setHistory).catch(() => setHistory([]));
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("lang") === language) return;
    url.searchParams.set("lang", language);
    window.history.replaceState(null, "", url);
  }, [language]);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(recipeHistoryStorageKey);
      if (!stored) return;
      const parsed = JSON.parse(stored) as RecipeHistoryItem[];
      setRecipeHistory(parsed.filter((item) => item.recipe?.version === "SpectraFit v1").slice(0, recipeHistoryLimit));
    } catch {
      setRecipeHistory([]);
    }
  }, []);

  useEffect(() => {
    return () => {
      uploadedImages.forEach((item) => URL.revokeObjectURL(item.objectUrl));
    };
  }, [uploadedImages]);

  useEffect(() => {
    let isMounted = true;

    async function loadDefaultLut() {
      try {
        const response = await fetch(defaultLutUrl);
        if (!response.ok) return;
        const text = await response.text();
        if (!isMounted) return;
        setLut(parseCubeLut(text, defaultLutName, text.length));
      } catch {
        // The page still works without the built-in preset.
      }
    }

    void loadDefaultLut();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadBuiltInLooks() {
      const loaded = await Promise.all(
        builtInLookDefinitions.map(async (definition): Promise<BuiltInLookState> => {
          try {
            const response = await fetch(definition.url);
            if (!response.ok) {
              throw new Error(`Unable to load ${definition.fileName}`);
            }
            const text = await response.text();
            const parsed = parseCubeLut(text, definition.fileName, text.length);
            const recipe = fitMatrixRecipe(parsed);
            return {
              definition,
              lut: parsed,
              recipe,
              recipeText: formatSpectraFitRecipe(recipe),
              error: null
            };
          } catch (event) {
            return {
              definition,
              lut: null,
              recipe: null,
              recipeText: "",
              error: event instanceof Error ? event.message : "Unable to load this look."
            };
          }
        })
      );

      if (isMounted) {
        setBuiltInLooks(loaded);
      }
    }

    void loadBuiltInLooks();

    return () => {
      isMounted = false;
    };
  }, []);

  const summary = useMemo(() => summarizeLut(lut), [lut]);
  const fittedRecipe = useMemo(() => (lut ? fitMatrixRecipe(lut) : null), [lut]);
  const fittedRecipeText = useMemo(
    () => (fittedRecipe ? formatSpectraFitRecipe(fittedRecipe) : ""),
    [fittedRecipe]
  );
  const creatorRecipeText = useMemo(
    () => (creatorRecipe ? formatSpectraFitRecipe(creatorRecipe) : ""),
    [creatorRecipe]
  );
  const activeRenderRecipe = activeTab === "create" ? creatorRecipe : importedRecipe;
  const patchRecipe = activeTab === "create" ? creatorRecipe : fittedRecipe;
  const activePatches = useMemo(() => {
    return colorPatches.map((patch) => ({
      ...patch,
      mapped: lut ? applyLutToRgb(patch.rgb, lut, strength) : patch.rgb,
      fitted: patchRecipe ? applySpectraFitRecipe(patch.rgb, patchRecipe) : patch.rgb
    }));
  }, [lut, patchRecipe, strength]);

  useEffect(() => {
    if (fittedRecipe) {
      setCreatorRecipe(fittedRecipe);
    }
  }, [fittedRecipe]);

  useEffect(() => {
    const sourceCanvas = sourceCanvasRef.current;
    const outputCanvas = outputCanvasRef.current;
    const compareOriginalCanvas = compareOriginalCanvasRef.current;
    const compareLutCanvas = compareLutCanvasRef.current;
    const compareRecipeCanvas = compareRecipeCanvasRef.current;

    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const width = 1280;
      const height = Math.round((image.height / image.width) * width);
      const scratchCanvas = document.createElement("canvas");
      scratchCanvas.width = width;
      scratchCanvas.height = height;
      const scratchContext = scratchCanvas.getContext("2d");
      if (!scratchContext) return;

      const canvases = [
        sourceCanvas,
        outputCanvas,
        compareOriginalCanvas,
        compareLutCanvas,
        compareRecipeCanvas
      ].filter((canvas): canvas is HTMLCanvasElement => Boolean(canvas));

      canvases.forEach((canvas) => {
        canvas.width = width;
        canvas.height = height;
      });

      const sourceContext = sourceCanvas?.getContext("2d");
      const outputContext = outputCanvas?.getContext("2d");

      scratchContext.drawImage(image, 0, 0, width, height);
      const sourceData = scratchContext.getImageData(0, 0, width, height);
      const rendered = applyLutToImageData(sourceData, lut, strength);

      if (sourceContext) {
        sourceContext.drawImage(image, 0, 0, width, height);
      }
      if (outputContext) {
        outputContext.putImageData(rendered, 0, 0);
      }

      const compareOriginalContext = compareOriginalCanvas?.getContext("2d");
      const compareLutContext = compareLutCanvas?.getContext("2d");
      const compareRecipeContext = compareRecipeCanvas?.getContext("2d");

      if (compareOriginalContext) {
        compareOriginalContext.drawImage(image, 0, 0, width, height);
      }
      if (compareLutContext) {
        compareLutContext.putImageData(rendered, 0, 0);
      }
      if (compareRecipeContext) {
        const recipeRendered = activeRenderRecipe
          ? applySpectraFitRecipeToImageData(sourceData, activeRenderRecipe)
          : sourceData;
        compareRecipeContext.putImageData(recipeRendered, 0, 0);
      }
    };
    image.src = sampleImage;
  }, [activeRenderRecipe, activeTab, lut, strength]);

  const copyRecipe = useCallback(async () => {
    if (!creatorRecipeText) return;
    try {
      await navigator.clipboard.writeText(creatorRecipeText);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 1400);
    } catch {
      setCopyState("failed");
      window.setTimeout(() => setCopyState("idle"), 1800);
    }
  }, [creatorRecipeText]);

  const importRecipe = useCallback(() => {
    setRecipeImportError(null);
    try {
      const parsed = parseSpectraFitRecipe(recipeImportText);
      setImportedRecipe(parsed);
      saveRecipeHistoryItem(setRecipeHistory, recipeImportText, parsed);
    } catch (event) {
      setImportedRecipe(null);
      setRecipeImportError(event instanceof Error ? event.message : "Unable to parse this recipe.");
    }
  }, [recipeImportText]);

  const clearImportedRecipe = useCallback(() => {
    setRecipeImportText("");
    setImportedRecipe(null);
    setRecipeImportError(null);
  }, []);

  const useSampleRecipe = useCallback(() => {
    setRecipeImportText(defaultRecipeText);
    setImportedRecipe(defaultRecipe);
    setRecipeImportError(null);
    saveRecipeHistoryItem(setRecipeHistory, defaultRecipeText, defaultRecipe);
  }, []);

  const useGeneratedRecipe = useCallback(() => {
    if (!fittedRecipeText || !fittedRecipe) return;
    setRecipeImportText(fittedRecipeText);
    setImportedRecipe(fittedRecipe);
    setRecipeImportError(null);
    saveRecipeHistoryItem(setRecipeHistory, fittedRecipeText, fittedRecipe);
  }, [fittedRecipe, fittedRecipeText]);

  const loadRecipeHistoryItem = useCallback((item: RecipeHistoryItem) => {
    setRecipeImportText(item.text);
    setImportedRecipe(item.recipe);
    setRecipeImportError(null);
  }, []);

  const copyImportedRecipe = useCallback(async () => {
    if (!importedRecipe) return;
    try {
      await navigator.clipboard.writeText(formatSpectraFitRecipe(importedRecipe));
      setImportCopyState("copied");
      window.setTimeout(() => setImportCopyState("idle"), 1400);
    } catch {
      setImportCopyState("failed");
      window.setTimeout(() => setImportCopyState("idle"), 1800);
    }
  }, [importedRecipe]);

  const tuneCreatorNumber = useCallback((field: RecipeNumberField, value: number) => {
    setCreatorRecipe((current) => current ? tuneSpectraFitRecipe(current, { field, value }) : current);
  }, []);

  const tuneCreatorBias = useCallback((index: 0 | 1 | 2, value: number) => {
    setCreatorRecipe((current) => current ? tuneSpectraFitRecipe(current, { field: "rgbBias", index, value }) : current);
  }, []);

  const resetCreatorRecipe = useCallback(() => {
    if (fittedRecipe) {
      setCreatorRecipe(fittedRecipe);
    }
  }, [fittedRecipe]);

  const copyLookRecipe = useCallback(async (look: BuiltInLookState) => {
    if (!look.recipeText) return;
    try {
      await navigator.clipboard.writeText(look.recipeText);
      setCopiedLookId(look.definition.id);
      window.setTimeout(() => setCopiedLookId(null), 1400);
    } catch {
      setCopiedLookId(null);
    }
  }, []);

  const selectBuiltInLook = useCallback((look: BuiltInLookState) => {
    if (!look.lut) return;
    setError(null);
    setLut(look.lut);
  }, []);

  const applySharedRecipe = useCallback(() => {
    setApplyRecipeError(null);
    try {
      setApplyRecipe(parseSpectraFitRecipe(applyRecipeText));
    } catch (event) {
      setApplyRecipeError(event instanceof Error ? event.message : t.applyErrorFallback);
    }
  }, [applyRecipeText, t.applyErrorFallback]);

  const clearUploadedImages = useCallback(() => {
    uploadedImages.forEach((item) => URL.revokeObjectURL(item.objectUrl));
    setUploadedImages([]);
  }, [uploadedImages]);

  const downloadProcessedImage = useCallback(async (item: UploadedImageItem) => {
    const blob = await renderRecipeToBlob(item.file, applyRecipe);
    downloadBlob(blob, processedFileName(item.fileName));
  }, [applyRecipe]);

  const downloadAllProcessedImages = useCallback(async () => {
    for (const item of uploadedImages) {
      const blob = await renderRecipeToBlob(item.file, applyRecipe);
      downloadBlob(blob, processedFileName(item.fileName));
      await new Promise((resolve) => window.setTimeout(resolve, 120));
    }
  }, [applyRecipe, uploadedImages]);

  const readyLookCount = builtInLooks.filter((look) => look.recipe).length;
  const mastheadLead = activeTab === "restore" ? t.restoreLead : activeTab === "apply" ? t.applyLead : t.createLead;

  return (
    <main className="shell">
      <section className="masthead">
        <div>
          <div className="eyebrow">SpectraCam Lab</div>
          <h1>{t.title}</h1>
          <p className="mastheadLead">{mastheadLead}</p>
        </div>
        <div className="mastheadActions">
          <div className="languageSwitch" role="group" aria-label="Language">
            <button
              className={language === "zh" ? "active" : ""}
              type="button"
              onClick={() => setLanguage("zh")}
            >
              中文
            </button>
            <button
              className={language === "en" ? "active" : ""}
              type="button"
              onClick={() => setLanguage("en")}
            >
              EN
            </button>
          </div>
        </div>
      </section>

      <nav className="tabBar" aria-label="SpectraFit workflow">
        <button
          className={activeTab === "restore" ? "active" : ""}
          type="button"
          onClick={() => setActiveTab("restore")}
        >
          {t.restoreTab}
        </button>
        <button
          className={activeTab === "apply" ? "active" : ""}
          type="button"
          onClick={() => setActiveTab("apply")}
        >
          {t.applyTab}
        </button>
        <button
          className={activeTab === "create" ? "active" : ""}
          type="button"
          onClick={() => setActiveTab("create")}
        >
          {t.createTab}
        </button>
      </nav>

      {activeTab === "restore" ? (
        <section className="lookLibrary">
          <div className="lookLibraryHeader">
            <div>
              <span>{t.lookLibraryTitle}</span>
              <p>{t.lookLibraryLead}</p>
            </div>
            <strong>{t.lookLibraryStatus(String(readyLookCount), String(builtInLooks.length))}</strong>
          </div>

          <div className="lookTable">
            <div className="lookTableHead">
              <span>{t.rowNumber}</span>
              <span>{t.name}</span>
              <span>{t.original}</span>
              <span>{t.originalLut}</span>
              <span>{t.recoveredRecipe}</span>
              <span>{t.sharePanelTitle}</span>
            </div>
            <div className="lookRows">
              {builtInLooks.map((look, index) => (
                <LookLibraryRow
                  key={look.definition.id}
                  index={index + 1}
                  look={look}
                  labels={{
                    copied: t.copied,
                    copyLook: t.copyLook,
                    fitScore: t.fitScore,
                    loading: t.loadingLook,
                    maxError: t.maxError,
                    meanError: t.meanError,
                    sourceSample: t.sourceSample,
                    waiting: t.waiting
                  }}
                  copied={copiedLookId === look.definition.id}
                  onCopy={() => void copyLookRecipe(look)}
                />
              ))}
            </div>
          </div>
        </section>
      ) : activeTab === "apply" ? (
        <section className="applyWorkspace">
          <div className="applyHero">
            <div>
              <span>{t.applyPhotosTitle}</span>
              <p>{t.applyPhotosLead}</p>
            </div>
            <strong>{t.photosReady(String(uploadedImages.length))}</strong>
          </div>

          <div className="applyControls">
            <div className="recipeImportBox primaryImportBox">
              <div className="importHeroHeader">
                <div>
                  <span>{t.sharePanelTitle}</span>
                  <p>{t.sharePanelSubtitle}</p>
                </div>
                <div className="importHeroActions">
                  <button type="button" onClick={() => setApplyRecipeText(defaultRecipeText)}>
                    {t.useSampleRecipe}
                  </button>
                  <button type="button" onClick={applySharedRecipe}>
                    {t.applySharedLook}
                  </button>
                </div>
              </div>
              <textarea
                className="recipeTextarea heroRecipeTextarea"
                placeholder={t.recipePlaceholder}
                value={applyRecipeText}
                onChange={(event) => setApplyRecipeText(event.target.value)}
              />
              {applyRecipeError ? <div className="errorBox">{applyRecipeError}</div> : null}
            </div>

            <div className="imageUploadCard">
              <input ref={imageInputRef} type="file" accept="image/*" multiple onChange={handleImageInput} />
              <span>{t.uploadImages}</span>
              <p>{t.noPhotos}</p>
              <button type="button" onClick={() => imageInputRef.current?.click()}>
                {uploadedImages.length === 0 ? t.uploadImages : t.addImages}
              </button>
              <button type="button" onClick={downloadAllProcessedImages} disabled={uploadedImages.length === 0}>
                {t.downloadAll}
              </button>
              <button type="button" onClick={clearUploadedImages} disabled={uploadedImages.length === 0}>
                {t.clearImages}
              </button>
            </div>
          </div>

          <div className="applyTable">
            <div className="applyTableHead">
              <span>{t.rowNumber}</span>
              <span>{t.name}</span>
              <span>{t.original}</span>
              <span>{t.processed}</span>
              <span>Actions</span>
            </div>
            {uploadedImages.length === 0 ? (
              <div className="emptyApplyState">{t.noPhotos}</div>
            ) : (
              <div className="applyRows">
                {uploadedImages.map((item, index) => (
                  <ApplyImageRow
                    key={item.id}
                    index={index + 1}
                    item={item}
                    recipe={applyRecipe}
                    labels={{
                      downloadImage: t.downloadImage,
                      original: t.original,
                      processed: t.processed
                    }}
                    onDownload={() => void downloadProcessedImage(item)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      ) : (
      <section className="workspace creatorWorkspace">
        <aside className="sidePanel">
          <div
            className={`dropzone ${isDragging ? "dragging" : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input ref={fileInputRef} type="file" accept=".cube" onChange={handleFileInput} />
            <span className="dropLabel">{t.dropLabel}</span>
            <strong>{lut?.title ?? t.uploadLut}</strong>
            <p>{lut ? t.parsedLocal : t.dragLut}</p>
          </div>

          {error ? <div className="errorBox">{error}</div> : null}

          <div className="presetLutBox">
            <div className="historyHeader">
              <span>{t.presetSpectraTitle}</span>
            </div>
            <p>{t.presetSpectraLead}</p>
            <div className="presetLutList">
              {builtInLooks.map((look) => (
                <button
                  className={lut?.title === look.lut?.title ? "active" : ""}
                  key={look.definition.id}
                  type="button"
                  disabled={!look.lut}
                  onClick={() => selectBuiltInLook(look)}
                >
                  <strong>{look.definition.displayName}</strong>
                  <span>{look.lut ? look.definition.sourceProfile : t.presetLoading}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="metricGrid">
            <Metric label={t.titleMetric} value={lut?.title ?? t.waiting} />
            <Metric label={t.grid} value={lut ? `${lut.dimension} x ${lut.dimension} x ${lut.dimension}` : "--"} />
            <Metric label={t.entries} value={lut ? lut.entries.toLocaleString() : "--"} />
            <Metric label={t.size} value={lut ? formatBytes(lut.fileSize) : "--"} />
          </div>

          <div className="controlBlock">
            <div className="controlLabel">
              <span>{t.lutStrength}</span>
              <strong>{strengthPercent}%</strong>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={strength}
              onChange={(event) => setStrength(Number(event.target.value))}
            />
          </div>

          <div className="summaryBox">
            <span>{t.analysisNote}</span>
            <p>{summary}</p>
          </div>

          <div className="historyBox">
            <div className="historyHeader">
              <span>{t.recentLuts}</span>
              <button type="button" onClick={clearHistory} disabled={history.length === 0}>
                {t.clear}
              </button>
            </div>
            {history.length === 0 ? (
              <p className="historyEmpty">{t.historyEmpty}</p>
            ) : (
              <div className="historyList">
                {history.map((item) => (
                  <button
                    className="historyItem"
                    key={item.id}
                    type="button"
                    onClick={() => loadHistoryItem(item)}
                  >
                    <strong>{item.title}</strong>
                    <span>
                      {item.dimension} grid · {formatBytes(item.fileSize)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

        <section className="mainPanel">
          <div className="comparisonSection creatorComparison topComparison">
            <div className="panelHeader comparisonHeader">
              <span>{t.recoveryComparison}</span>
              <small>{t.comparisonLegend}</small>
            </div>
            <div className="comparisonGrid">
              <ComparisonCard
                title={t.original}
                subtitle={t.sourceSample}
                canvasRef={compareOriginalCanvasRef}
                meta={[
                  [t.image, t.sampleName],
                  [t.transform, t.none],
                  [t.role, t.baselineReference]
                ]}
              />
              <ComparisonCard
                title={t.originalLut}
                subtitle={lut ? t.mix(strengthPercent) : t.neutral}
                canvasRef={compareLutCanvasRef}
                meta={[
                  ["LUT", lut?.title ?? t.defaultPreset],
                  [t.grid, lut ? `${lut.dimension} x ${lut.dimension} x ${lut.dimension}` : "--"],
                  [t.strength, `${strengthPercent}%`]
                ]}
              />
              <ComparisonCard
                title={t.recoveredRecipe}
                subtitle={creatorRecipe ? t.fitScore(creatorRecipe.fitScore.toFixed(0)) : t.waiting}
                canvasRef={compareRecipeCanvasRef}
                meta={[
                  [t.version, creatorRecipe?.version ?? "--"],
                  [t.meanError, creatorRecipe ? creatorRecipe.meanRgbError.toFixed(2) : "--"],
                  [t.maxError, creatorRecipe ? creatorRecipe.maxRgbError.toFixed(2) : "--"]
                ]}
              />
            </div>
          </div>

          <div className="analysisGrid">
            <div className="analysisCard cubeCard">
              <div className="panelHeader">
                <span>{t.colorTransform}</span>
                <small>{lut ? t.mappedCloud : t.neutralCube}</small>
              </div>
              <ColorCube lut={lut} />
            </div>

            <div className="analysisCard">
              <div className="panelHeader">
                <span>{t.colorResponse}</span>
                <small>{t.responseLegend}</small>
              </div>
              <div className="patchList">
                {activePatches.map((patch) => (
                  <div className="patchRow" key={patch.name}>
                    <span>{patch.name}</span>
                    <div className="patchPair">
                      <i style={{ backgroundColor: rgbCss(patch.rgb) }} />
                      <i style={{ backgroundColor: rgbCss(patch.mapped) }} />
                      <i style={{ backgroundColor: rgbCss(patch.fitted) }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </section>

        <aside className="shareColumn">
          <div className="analysisCard recipeCard shareRecipeCard">
            <div className="panelHeader">
              <span>{t.sharePanelTitle}</span>
              <small>{creatorRecipe ? t.fitScore(creatorRecipe.fitScore.toFixed(0)) : t.waiting}</small>
            </div>
            {creatorRecipe ? (
              <div className="recipeBody">
                <p className="shareHelpText">{t.sharePanelSubtitle}</p>
                <div className="fitMetrics">
                  <Metric label={t.meanError} value={creatorRecipe.meanRgbError.toFixed(2)} />
                  <Metric label={t.maxError} value={creatorRecipe.maxRgbError.toFixed(2)} />
                </div>
                <pre>{creatorRecipeText}</pre>
                <button className="copyButton" type="button" onClick={copyRecipe}>
                  {copyState === "copied" ? t.copied : copyState === "failed" ? t.copyFailed : t.copyRecipe}
                </button>
              </div>
            ) : (
              <div className="recipeEmpty">{t.sharePanelEmpty}</div>
            )}
          </div>

          <div className="analysisCard tuneCard">
            <div className="panelHeader">
              <span>{t.tuneRecipe}</span>
              <button type="button" onClick={resetCreatorRecipe} disabled={!fittedRecipe}>
                {t.resetFit}
              </button>
            </div>
            {creatorRecipe ? (
              <div className="tuneBody">
                <TuneSlider label={t.exposure} value={creatorRecipe.exposure} min={-80} max={80} step={0.5} onChange={(value) => tuneCreatorNumber("exposure", value)} />
                <TuneSlider label={t.contrast} value={creatorRecipe.contrast} min={-80} max={80} step={0.5} onChange={(value) => tuneCreatorNumber("contrast", value)} />
                <TuneSlider label={t.saturation} value={creatorRecipe.saturation} min={-100} max={150} step={0.5} onChange={(value) => tuneCreatorNumber("saturation", value)} />
                <TuneSlider label={t.temperature} value={creatorRecipe.temperature} min={-60} max={60} step={0.5} onChange={(value) => tuneCreatorNumber("temperature", value)} />
                <TuneSlider label={t.tint} value={creatorRecipe.tint} min={-60} max={60} step={0.5} onChange={(value) => tuneCreatorNumber("tint", value)} />
                <TuneSlider label={t.redBias} value={creatorRecipe.rgbBias[0]} min={-80} max={80} step={0.5} onChange={(value) => tuneCreatorBias(0, value)} />
                <TuneSlider label={t.greenBias} value={creatorRecipe.rgbBias[1]} min={-80} max={80} step={0.5} onChange={(value) => tuneCreatorBias(1, value)} />
                <TuneSlider label={t.blueBias} value={creatorRecipe.rgbBias[2]} min={-80} max={80} step={0.5} onChange={(value) => tuneCreatorBias(2, value)} />
              </div>
            ) : (
              <div className="recipeEmpty">{t.recipeEmpty}</div>
            )}
          </div>
        </aside>
      </section>
      )}
    </main>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function rgbCss(rgb: [number, number, number]) {
  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
}

function TuneSlider({
  label,
  value,
  min,
  max,
  step,
  onChange
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="tuneRow">
      <div>
        <span>{label}</span>
        <strong>{formatTuneValue(value)}</strong>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

function formatTuneValue(value: number) {
  const rounded = Math.abs(value) >= 10 ? value.toFixed(0) : value.toFixed(1);
  return value > 0 ? `+${rounded}` : rounded;
}

function saveRecipeHistoryItem(
  setRecipeHistory: React.Dispatch<React.SetStateAction<RecipeHistoryItem[]>>,
  text: string,
  recipe: SpectraFitRecipe
) {
  setRecipeHistory((current) => {
    const normalized = text.trim();
    const next = [
      {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        createdAt: Date.now(),
        text: normalized,
        recipe
      },
      ...current.filter((item) => item.text.trim() !== normalized)
    ].slice(0, recipeHistoryLimit);

    try {
      window.localStorage.setItem(recipeHistoryStorageKey, JSON.stringify(next));
    } catch {
      // History is an enhancement; the import flow should still work.
    }

    return next;
  });
}

function ComparisonCard({
  title,
  subtitle,
  canvasRef,
  meta,
  action
}: {
  title: string;
  subtitle: string;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  meta: [string, string][];
  action?: React.ReactNode;
}) {
  return (
    <div className="comparisonCard">
      <div className="panelHeader">
        <span>{title}</span>
        <small>{subtitle}</small>
      </div>
      <canvas ref={canvasRef} />
      <div className="comparisonMeta">
        {meta.map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
      {action ? <div className="comparisonAction">{action}</div> : null}
    </div>
  );
}

function LookLibraryRow({
  index,
  look,
  labels,
  copied,
  onCopy
}: {
  index: number;
  look: BuiltInLookState;
  labels: {
    copied: string;
    copyLook: string;
    fitScore: (value: string) => string;
    loading: string;
    maxError: string;
    meanError: string;
    sourceSample: string;
    waiting: string;
  };
  copied: boolean;
  onCopy: () => void;
}) {
  const originalCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const lutCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const recipeCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const width = 520;
      const height = Math.round((image.height / image.width) * width);
      const scratchCanvas = document.createElement("canvas");
      scratchCanvas.width = width;
      scratchCanvas.height = height;
      const scratchContext = scratchCanvas.getContext("2d");
      if (!scratchContext) return;

      scratchContext.drawImage(image, 0, 0, width, height);
      const sourceData = scratchContext.getImageData(0, 0, width, height);
      const lutData = applyLutToImageData(sourceData, look.lut, builtInLookStrength);
      const recipeData = look.recipe ? applySpectraFitRecipeToImageData(sourceData, look.recipe) : sourceData;

      drawPreviewCanvas(originalCanvasRef.current, sourceData);
      drawPreviewCanvas(lutCanvasRef.current, lutData);
      drawPreviewCanvas(recipeCanvasRef.current, recipeData);
    };
    image.src = sampleImage;
  }, [look.lut, look.recipe]);

  return (
    <article className="lookRow">
      <div className="lookIndex">{String(index).padStart(2, "0")}</div>

      <div className="lookNameCell">
        <strong>{look.definition.displayName}</strong>
        <span>{look.definition.sourceProfile}</span>
        <small>{look.definition.fileName}</small>
      </div>

      <LookPreviewCanvas label="Original" note={labels.sourceSample} canvasRef={originalCanvasRef} />
      <LookPreviewCanvas
        label="Original LUT"
        note={look.lut ? `${look.lut.dimension} grid` : labels.loading}
        canvasRef={lutCanvasRef}
      />
      <LookPreviewCanvas
        label="Spectrum Recovery"
        note={look.recipe ? labels.fitScore(look.recipe.fitScore.toFixed(0)) : labels.waiting}
        canvasRef={recipeCanvasRef}
      />

      <div className="lookShareCell">
        {look.error ? (
          <div className="lookError">{look.error}</div>
        ) : look.recipe ? (
          <>
            <div className="lookMetrics">
              <span>
                {labels.meanError}
                <strong>{look.recipe.meanRgbError.toFixed(2)}</strong>
              </span>
              <span>
                {labels.maxError}
                <strong>{look.recipe.maxRgbError.toFixed(2)}</strong>
              </span>
            </div>
            <pre>{look.recipeText}</pre>
            <button className="copyButton compactCopy" type="button" onClick={onCopy}>
              {copied ? labels.copied : labels.copyLook}
            </button>
          </>
        ) : (
          <div className="lookLoading">{labels.loading}</div>
        )}
      </div>
    </article>
  );
}

function LookPreviewCanvas({
  label,
  note,
  canvasRef
}: {
  label: string;
  note: string;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
}) {
  return (
    <div className="lookPreviewCell">
      <canvas ref={canvasRef} />
      <div>
        <span>{label}</span>
        <small>{note}</small>
      </div>
    </div>
  );
}

function drawPreviewCanvas(canvas: HTMLCanvasElement | null, imageData: ImageData) {
  if (!canvas) return;
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  canvas.getContext("2d")?.putImageData(imageData, 0, 0);
}

function ApplyImageRow({
  index,
  item,
  recipe,
  labels,
  onDownload
}: {
  index: number;
  item: UploadedImageItem;
  recipe: SpectraFitRecipe;
  labels: {
    downloadImage: string;
    original: string;
    processed: string;
  };
  onDownload: () => void;
}) {
  const originalCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const processedCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const image = new Image();
    image.onload = () => {
      const width = 720;
      const height = Math.round((image.height / image.width) * width);
      const scratchCanvas = document.createElement("canvas");
      scratchCanvas.width = width;
      scratchCanvas.height = height;
      const scratchContext = scratchCanvas.getContext("2d");
      if (!scratchContext) return;

      scratchContext.drawImage(image, 0, 0, width, height);
      const sourceData = scratchContext.getImageData(0, 0, width, height);
      const processedData = applySpectraFitRecipeToImageData(sourceData, recipe);

      drawPreviewCanvas(originalCanvasRef.current, sourceData);
      drawPreviewCanvas(processedCanvasRef.current, processedData);
    };
    image.src = item.objectUrl;
  }, [item.objectUrl, recipe]);

  return (
    <article className="applyRow">
      <div className="lookIndex">{String(index).padStart(2, "0")}</div>
      <div className="applyNameCell">
        <strong>{item.fileName}</strong>
        <span>{formatBytes(item.file.size)}</span>
      </div>
      <LookPreviewCanvas label={labels.original} note={item.file.type || "image"} canvasRef={originalCanvasRef} />
      <LookPreviewCanvas label={labels.processed} note="SpectraFit v1" canvasRef={processedCanvasRef} />
      <div className="applyActionCell">
        <button className="copyButton compactCopy" type="button" onClick={onDownload}>
          {labels.downloadImage}
        </button>
      </div>
    </article>
  );
}

async function renderRecipeToBlob(file: File, recipe: SpectraFitRecipe) {
  const image = await loadImageFromFile(file);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas is unavailable.");
  }

  context.drawImage(image, 0, 0, width, height);
  const sourceData = context.getImageData(0, 0, width, height);
  context.putImageData(applySpectraFitRecipeToImageData(sourceData, recipe), 0, 0);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Unable to export processed image."));
      }
    }, "image/png");
  });
}

function loadImageFromFile(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Unable to load image."));
    };
    image.src = url;
  });
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function processedFileName(fileName: string) {
  const dotIndex = fileName.lastIndexOf(".");
  const baseName = dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
  return `${baseName}-spectrafit.png`;
}
